import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BaseService } from 'app/shared/services/base.service';
import { CommonResponse } from 'app/types/common-response.type';
import {
    HomeDashboard,
    HomeFilters,
    HomeSection,
    HomeSections,
    HomeSlot,
    HomeSlotProduct,
    HomeSlotProductQuery,
    HomeSlotQuery,
} from 'app/types/home-dashboard.type';
import { Observable, catchError, map } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class HomeDashboardService extends BaseService {
    private readonly basePath = '/api/v1/dashboard/home';

    constructor(
        public _http: HttpClient,
        public _router: Router,
    ) {
        super(_router);
    }

    getHome(
        filters: HomeFilters,
        section: HomeSection,
    ): Observable<HomeDashboard> {
        const params = this.buildParams(filters).set('section', section);

        return this.get<HomeDashboard>(this.basePath, params);
    }

    getSections(
        filters: HomeFilters,
        sections: HomeSection[],
    ): Observable<HomeSections> {
        let params = this.buildParams(filters);
        sections.forEach((section) => {
            params = params.append('sections[]', section);
        });

        return this.get<HomeSections>(`${this.basePath}/sections`, params);
    }

    getSlot(filters: HomeFilters, slot: HomeSlotQuery): Observable<HomeSlot> {
        let params = this.buildParams(filters)
            .set('section', slot.section)
            .set('slot_day', slot.slotDay);
        if (slot.slotCycleId !== undefined) {
            params = params.set('slot_cycle_id', slot.slotCycleId.toString());
        }

        return this.get<HomeSlot>(`${this.basePath}/slot`, params);
    }

    getSlotProduct(query: HomeSlotProductQuery): Observable<HomeSlotProduct> {
        const params = new HttpParams()
            .set('subsidiary_id', query.subsidiaryId.toString())
            .set('product_id', query.productId.toString())
            .set('cycle_id', query.cycleId.toString())
            .set('slot_day', query.slotDay);

        return this.get<HomeSlotProduct>(`${this.basePath}/slot/product`, params);
    }

    private get<D>(path: string, params: HttpParams): Observable<D> {
        return this._http
            .get<CommonResponse<D>>(`${this._api}${path}`, { params })
            .pipe(
                catchError((error) => this.handleError(error)),
                map((response: CommonResponse<D>) => response.data),
            );
    }

    private buildParams(filters: HomeFilters): HttpParams {
        let params = new HttpParams()
            .set('from', filters.range.from)
            .set('to', filters.range.to);
        filters.subsidiaryIds.forEach((id) => {
            params = params.append('subsidiary_ids[]', id.toString());
        });
        if (filters.cycleId !== null) {
            params = params.set('cycle_id', filters.cycleId.toString());
        }
        if (filters.gateSubsidiaryId != null) {
            params = params.set(
                'gate_subsidiary_id',
                filters.gateSubsidiaryId.toString(),
            );
        }

        return params;
    }
}
